import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, Divider, Popover, CircularProgress } from '@mui/material';

import SortIcon from '@mui/icons-material/SortOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import ExportIcon from '../../../assets/Export.svg';
import DownloadIcon from '@mui/icons-material/Download';
import GameImage from '../../../assets/game_image.jpg';

import { TEAM_ICON_DEFAULT } from '../../../common/staticData';
import { SaveButton } from '../components/common';
import GameService from '../../../services/game.service';
import GameEditPage from './gameEditPage';
import GameEditMinutesPage from './gameEditMinutesPage';
import ExcelDataFiltering from '../../coach/ExcelDataFiltering';
import { XmlDataFilterGames, XmlDataFilterGamesShort } from '../components/xmldata';
import { downloadJsonFile, getFormattedDate } from '../components/utilities';

const GameListItem = ({ row, isHover, isPending = false, updateList, team, standing, t }) => {
    const navigate = useNavigate();
    const [editOpen, setEditOpen] = useState(false);
    const [editMinutesOpen, setEditMinutesOpen] = useState(false);
    const [exportExcel, setExportExcel] = useState(false);
    const [exportGate, setExportGate] = useState(false);
    const [exportSportShort, setExportSportShort] = useState(false);
    const [exportJson, setExportJson] = useState(false);
    const [playerTagList, setPlayerTagList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState({
        videoURL: '',
        mobileURL: ''
    });

    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const menuPopoverOpen = Boolean(menuAnchorEl);
    const menuPopoverId = menuPopoverOpen ? 'simple-popover' : undefined;

    const saveChanges = () => {
        updateList(
            {
                id: row.id,
                video_url: rowData.videoURL,
                mobile_video_url: rowData.mobileURL
            },
            false
        );
        GameService.updateGame({
            id: row.id,
            image: row.image,
            season_id: row.season_id,
            league_id: row.league_id,
            home_team_id: row.home_team_id,
            home_team_standing_id: row.home_team_standing_id,
            away_team_id: row.away_team_id,
            away_team_standing_id: row.away_team_standing_id,
            date: row.date,
            video_url: rowData.videoURL,
            mobile_video_url: rowData.mobileURL,
            mute_video: row.mute_video
        });
    };

    const handleCloseDialog = () => {
        setEditOpen(false);
    };
    const handleCloseMinutesDialog = () => {
        setEditMinutesOpen(false);
    };

    const handleShowPopover = (e) => {
        setMenuAnchorEl(e.currentTarget);
    };

    const handleClickEdit = () => {
        setEditOpen(true);
        setMenuAnchorEl(null);
    };

    const handleClickSportGate = () => {
        setLoading(true);
        setExportGate(true);
        setMenuAnchorEl(null);
    };

    const handleClickSportCodeShort = () => {
        setLoading(true);
        setExportSportShort(true);
        setMenuAnchorEl(null);
    };

    const handleClickExcel = async () => {
        await getAllInfosByGame(row);
        setExportExcel(true);
        setMenuAnchorEl(null);
    };

    const handleDownloadVideo = (url) => {
        setMenuAnchorEl(null);

        const link = document.createElement('a');

        link.href = url;
        link.target = '_blank';
        link.click();
    };

    const handleExportToJson = async () => {
        await getAllInfosByGame(row);
        setMenuAnchorEl(null);
        setExportJson(true);
    };

    const getAllInfosByGame = async (game) => {
        let team_id = null;

        setLoading(true);

        if (team === 'none') team_id = game.home_team_id;
        else team_id = game.home_team_name === team ? game.home_team_id : game.away_team_id;

        if (team_id !== null) {
            await GameService.getAllPlayerTagsByTeam(parseInt(team_id), parseInt(game.id)).then((res) => {
                setPlayerTagList(res);
            });
        }

        setLoading(false);
    };

    const handleChangePath = (gameId) => () => {
        navigate(`/new_coach/games/${btoa(gameId)}`);
    };

    useEffect(() => {
        if (exportJson) downloadJsonFile(playerTagList, setExportJson);
    }, [exportJson]);

    return (
        <Box
            sx={{
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '8px',
                backgroundColor: '#ffffff',
                borderRadius: '10px',
                transition: 'box-shadow 0.3s ease',
                fontWeight: 500,
                width: '100%',
                boxShadow: isHover ? '0px 4px 16px rgba(0, 0, 0, 0.1)' : 'none',
                cursor: 'pointer'
            }}
        >
            {/* Header row - Date, League, and Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem', fontWeight: 500, color: '#666' }}>
                    {getFormattedDate(row.date)} • {row.league_name}
                </Typography>
                {!isPending && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            width: '24px',
                            'svg path': { fill: '#A5A5A8' },
                            '&:hover': { 'svg path': { fill: '#17375E' } }
                        }}
                        onClick={handleShowPopover}
                    >
                        <SortIcon />
                    </Box>
                )}
            </Box>

            {/* Main content - Click to navigate */}
            <Box onClick={handleChangePath(row.id)} sx={{ display: 'flex', gap: '16px' }}>
                {/* Game image - hidden for now */}
                {/* <img
                    style={{ height: '80px', width: '120px', borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }}
                    src={row.image ? (row.image.includes(process.env.REACT_APP_S3_URI) ? row.image : process.env.REACT_APP_S3_URI + row.image) : GameImage}
                    alt="Game"
                /> */}

                {/* Teams and Score */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', width: '340px', flexShrink: 0 }}>
                    {/* Team names with scores */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }}>
                        {/* Home Team */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <img src={row.home_team_image ? row.home_team_image : TEAM_ICON_DEFAULT} style={{ width: '36px', height: '36px', flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: '#1a1b1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '160px', flexShrink: 0 }}>
                                {row.home_team_name}
                            </Typography>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#1a1b1d', width: '24px', textAlign: 'center', flexShrink: 0 }}>
                                {row.home_team_goals}
                            </Typography>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 500, color: '#888', width: '80px', flexShrink: 0 }}>
                                {row.home_team_standing_id && row.home_team_standing_id !== 13 && row.home_team_standing_name ? `(${row.home_team_standing_name})` : ''}
                            </Typography>
                        </Box>

                        {/* Away Team */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img src={row.away_team_image ? row.away_team_image : TEAM_ICON_DEFAULT} style={{ width: '36px', height: '36px', flexShrink: 0 }} />
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700, color: '#1a1b1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '160px', flexShrink: 0 }}>
                                {row.away_team_name}
                            </Typography>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '1rem', fontWeight: 700, color: '#1a1b1d', width: '24px', textAlign: 'center', flexShrink: 0 }}>
                                {row.away_team_goals}
                            </Typography>
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 500, color: '#888', width: '80px', flexShrink: 0 }}>
                                {row.away_team_standing_id && row.away_team_standing_id !== 13 && row.away_team_standing_name ? `(${row.away_team_standing_name})` : ''}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Players info - collapsible on smaller screens */}
                <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'center', flex: 2, minWidth: 0, borderLeft: '1px solid #eee', paddingLeft: '16px' }}>
                    {/* Home starters */}
                    <Box sx={{ marginBottom: '12px' }}>
                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 600, color: '#1a1b1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.home_team_started_players}
                        </Typography>
                        {row.home_team_bench_players && (
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.6rem', fontWeight: 400, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Subs: {row.home_team_bench_players}
                            </Typography>
                        )}
                    </Box>
                    {/* Away starters */}
                    <Box>
                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.65rem', fontWeight: 600, color: '#1a1b1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.away_team_started_players}
                        </Typography>
                        {row.away_team_bench_players && (
                            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.6rem', fontWeight: 400, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                Subs: {row.away_team_bench_players}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            <Popover
                id={menuPopoverId}
                open={menuPopoverOpen}
                anchorEl={menuAnchorEl}
                onClose={() => setMenuAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ '& .MuiPopover-paper': { width: '250px', borderRadius: '12px', border: '1px solid #E8E8E8' } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={handleClickEdit}>
                    <EditIcon />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Edit Game')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }}
                    onClick={() => {
                        setEditMinutesOpen(true);
                        setMenuAnchorEl(null);
                    }}
                >
                    <EditIcon />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Edit Players Minutes')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={handleClickSportGate}>
                    <img src={ExportIcon} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Export to Sportscode')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={handleClickSportCodeShort}>
                    <img src={ExportIcon} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Export to Sportscode Short version')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={handleClickExcel}>
                    <img src={ExportIcon} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Export to Excel ')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={() => handleDownloadVideo(row.video_url)}>
                    <DownloadIcon />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Download Video')}</Typography>
                </Box>
                <Divider sx={{ width: '100%' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '12px 20px', cursor: 'pointer' }} onClick={handleExportToJson}>
                    <img src={ExportIcon} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 500, color: '#1a1b1d' }}>{t('Export to JSON')}</Typography>
                </Box>
            </Popover>
            {isPending && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px', padding: '16px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px', minWidth: '200px' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1A1B1D' }}>{t('Video URL')}:</Typography>
                            <TextField
                                value={rowData.videoURL}
                                label=""
                                inputProps={{ 'aria-label': 'Without label' }}
                                variant="outlined"
                                onChange={(e) => setRowData({ ...rowData, videoURL: e.target.value })}
                                sx={{
                                    borderRadius: '10px',
                                    '& legend': { display: 'none' },
                                    '& fieldset': { top: 0 },
                                    '& .MuiOutlinedInput-input': { padding: '8px 16px' }
                                }}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 200px', minWidth: '200px' }}>
                            <Typography sx={{ fontSize: '0.7rem', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, color: '#1A1B1D' }}>{t('Mobile Video URL')}:</Typography>
                            <TextField
                                value={rowData.mobileURL}
                                label=""
                                inputProps={{ 'aria-label': 'Without label' }}
                                variant="outlined"
                                onChange={(e) => setRowData({ ...rowData, mobileURL: e.target.value })}
                                sx={{
                                    borderRadius: '10px',
                                    '& legend': { display: 'none' },
                                    '& fieldset': { top: 0 },
                                    '& .MuiOutlinedInput-input': { padding: '8px 16px' }
                                }}
                                fullWidth
                            />
                        </Box>
                    </Box>
                    <SaveButton disabled={rowData.videoURL.length === 0 && rowData.mobileURL.length === 0} onClick={saveChanges} sx={{ height: '32px', fontSize: '0.7rem', alignSelf: 'flex-end' }}>
                        + {t('Update')}
                    </SaveButton>
                </Box>
            )}
            <GameEditPage open={editOpen} onClose={handleCloseDialog} game={row} updateGameList={updateList} standingList={standing} />
            <GameEditMinutesPage open={editMinutesOpen} onClose={handleCloseMinutesDialog} game={row} updateGameList={updateList} standingList={standing} />
            {exportGate && <XmlDataFilterGames game={row} setXML={setExportGate} setLoading={setLoading} />}
            {exportSportShort && <XmlDataFilterGamesShort game={row} setXML={setExportSportShort} setLoading={setLoading} />}
            {exportExcel && <ExcelDataFiltering team={playerTagList} setExcelData={setExportExcel} />}
            {loading && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                >
                    <CircularProgress />
                </div>
            )}
        </Box>
    );
};

export default GameListItem;
